import { SxProps, Theme, FilledInputProps, InputProps, OutlinedInputProps } from '@mui/material';
import React from 'react';

import { DelayedSearch, DelayedSearchProps } from './delayed-search';
import { GeneralConstants } from '../constants/general-constants';
import { Brand } from '../model/cris/brand';
import { Store } from '../model/cris/store';
import { KnpPaginationData } from '../model/knp-pagination-data';
import { searchService } from '../services/search-service';

interface StorePickerProps {
    componentsProps?: DelayedSearchProps<Store, false>['componentsProps'];
    onOpen?: DelayedSearchProps<Store, false>['onOpen'];
    onClose?: DelayedSearchProps<Store, false>['onClose'];
    placeholder?: DelayedSearchProps<Store, false>['placeholder'];
    fullWidth?: DelayedSearchProps<Store, false>['fullWidth'];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    InputProps?: Partial<FilledInputProps> | Partial<InputProps> | Partial<OutlinedInputProps>;
    useShortenedDisplayValueForInput?: boolean;
    useDynamicWidth?: boolean;
    sx?: SxProps<Theme>;
    value?: Store;
    size?: 'medium' | 'small';
    /** @var unification project flag */
    up?: boolean;
    label?: React.ReactNode;

    onChange: (event: React.ChangeEvent<unknown>, value?: Store) => void;
    getOptionDisabled?: (option: Store) => boolean;
}

interface StorePickerState {
    store: Store | null;
}

export class StorePicker extends React.Component<StorePickerProps, StorePickerState> {
    public constructor(props: Readonly<StorePickerProps> | StorePickerProps) {
        super(props);

        this.state = {
            store: props.value || GeneralConstants.COMPONENT_VALUE_NULL,
        };
    }

    public render(): React.ReactNode {
        const { useShortenedDisplayValueForInput, ...remaining } = this.props;

        const getOptionLabel = useShortenedDisplayValueForInput
            ? StorePicker.makeShortenedStoreLabel
            : StorePicker.makeStoreLabel;
        const renderOption: DelayedSearchProps<Store, false>['renderOption'] = useShortenedDisplayValueForInput
            ? (props2, option) => <li {...props2}>{StorePicker.makeStoreLabel(option)}</li>
            : undefined;

        return (
            <DelayedSearch
                {...remaining}
                name='store'
                freeSolo={false}
                onChange={(event: React.ChangeEvent<unknown>, value?: Store) => {
                    this.setState({ store: value || GeneralConstants.COMPONENT_VALUE_NULL });
                    this.props.onChange(event, value);
                }}
                value={this.props.value || this.state.store}
                label={this.props.label ?? 'Pick A Store'}
                onSearch={(data) => {
                    if (this.props.up) {
                        return searchService.searchUp(data, 1, GeneralConstants.PAGE_SIZE_LARGE);
                    }

                    return searchService.search(data, 1, GeneralConstants.PAGE_SIZE_LARGE);
                }}
                mapDataToOptions={(data: KnpPaginationData<Brand>) => {
                    const allStores = data.data.map((brand) => {
                        if (!brand.stores) {
                            return [];
                        }

                        return brand.stores.map((store) => {
                            const result: Store = { ...store, brand: { ...brand, stores: undefined } };

                            return result;
                        });
                    });

                    return allStores.reduce((prev, current) => current.concat(prev), []);
                }}
                getOptionLabel={getOptionLabel}
                renderOption={renderOption}
            />
        );
    }

    private static makeStoreLabel(store?: Store): string {
        if (!store) {
            return '';
        }

        return `[${store.id}] ${store.name} (${store.brand?.name || '[none]'})`;
    }

    private static makeShortenedStoreLabel(store?: Store): string {
        if (!store) {
            return '';
        }

        return `${store.name}`;
    }
}
